// ============================================================================
// RHSubscriptionBase
//
// Base class for all client-side stores. Owns the WeakMap/Set subscription
// system that was previously duplicated across RHProcurementStore,
// RHOfferStore, and RHNotificationStore.
//
// Usage:
//   store.subscribe(this, (changedIds) => { ... })
//   store.unsubscribe(this)
//
// Subscribe in viewWillAppear BEFORE the first data read, unsubscribe in
// viewWillDisappear. The WeakMap uses the subscriber object as key so
// unsubscribing requires no token — just pass `this`.
//
// The handler type parameter HandlerType is the handler function signature each
// subclass uses. RHProcurementStore uses (changedIds: string[]) => void,
// RHOfferStore uses (changedProcurementIds?: string[]) => void; an empty or
// omitted list remains the compatibility signal for a non-granular change.
// RHNotificationStore uses (affectedEntityIds: string[]) => void.
// ============================================================================

export abstract class RHSubscriptionBase<HandlerType extends (...args: never[]) => void> {
    
    private _subscriberMap = new WeakMap<object, HandlerType>()
    private _handlers = new Set<HandlerType>()
    
    
    subscribe(owner: object, handler: HandlerType) {
        // If the owner is re-subscribing, remove the old handler first to
        // avoid duplicate notifications.
        this._removeHandlerForOwner(owner)
        this._subscriberMap.set(owner, handler)
        this._handlers.add(handler)
    }
    
    
    unsubscribe(owner: object) {
        this._removeHandlerForOwner(owner)
    }
    
    
    private _removeHandlerForOwner(owner: object) {
        const existing = this._subscriberMap.get(owner)
        if (existing) {
            this._handlers.delete(existing)
            this._subscriberMap.delete(owner)
        }
    }
    
    
    protected _notifySubscribers(...args: Parameters<HandlerType>) {
        this._handlers.forEach(handler => {
            try {
                handler(...args)
            }
            catch (error) {
                console.error(this.constructor.name + ": subscriber handler threw", error)
            }
        })
    }
    
}
